import { Directive, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Account, AccountService } from '../../core/account';
import { GetAppConfigResponse, ProductSettingsService } from '../../core/product-settings';
import { AccountRouteParams } from '../../core/routeparams';
import { SiteService } from '../../core/site';
import { Template } from '../../core/template';
import { CreateFromTemplateService } from './create-from-template.service';

enum LIMITS {
  THREE_COLUMNS = 1740,
  TWO_COLUMNS = 1270,
}

@Directive()
export class BaseComponent implements OnDestroy {
  readonly marketingTemplates$: Observable<Template[]> = this.createFromTemplateService.marketingTemplates$;
  readonly partnerTemplates$: Observable<Template[]> = this.createFromTemplateService.partnerTemplates$;
  readonly easyEcommerceTemplates$: Observable<Template[]> = this.createFromTemplateService.easyEcommerceTemplates$;
  readonly standardTemplates$: Observable<Template[]> = this.createFromTemplateService.standardTemplates$;
  readonly nonDistributedTemplates$: Observable<Template[]> = this.createFromTemplateService.nonDistributedTemplates$;
  readonly isSuperAdmin$: Observable<boolean> = this.createFromTemplateService.isSuperAdmin$;
  readonly isDistributedTemplatesFeatureEnabled$: Observable<boolean> =
    this.createFromTemplateService.isDistributedTemplatesFeatureEnabled$;

  protected appConfig$: Observable<GetAppConfigResponse>;

  protected subscriptions: Subscription[] = [];
  protected accountGroupId: string;
  columns: number;
  rowHeight: number;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected siteService: SiteService,
    protected accountService: AccountService,
    protected createFromTemplateService: CreateFromTemplateService,
    protected productSettingsService: ProductSettingsService,
  ) {
    this.rowHeight = 484;
    this.accountGroupId = (this.route.snapshot.params as AccountRouteParams).accountGroupId;
    this.createFromTemplateService.setAccountGroupId(this.accountGroupId);
    this.appConfig$ = this.accountService.result().pipe(
      map((account: Account) => account.partnerId),
      switchMap((partnerId: string) => this.productSettingsService.getAppConfig(partnerId)),
      catchError(() => {
        return of(
          GetAppConfigResponse.fromProto({
            config: {
              showAdvancedFeatures: true,
              showIncludedTemplates: true,
              showDomainsFeatures: true,
              showImportFeatures: true,
              showBackupsFeatures: true,
              showStagingFeatures: true,
            },
          }),
        );
      }),
    );
    this.subscriptions.push(
      this.siteService.lookup(this.accountGroupId).subscribe(
        (s) => this.router.navigateByUrl(`/site/${s.siteId}/overview`),
        () => {
          this.createFromTemplateService.setLoading(false);
        },
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  calcColumns(width: number): void {
    if (width >= LIMITS.THREE_COLUMNS) {
      this.columns = 3;
    } else if (width >= LIMITS.TWO_COLUMNS) {
      this.columns = 2;
    } else {
      this.columns = 1;
    }
  }
}
